import * as XLSX from "xlsx";
import { apiFetch } from "../../../context/AuthContext";

const useAllowanceDeductionImport = ({ t, showAlert, refreshData }) => {
  const API_BASE_URL = process.env.REACT_APP_BASE_URL;

  const handleOpenAllowanceDeductionImport = () => {
    const input = document.getElementById("allowance-deduction-import-input");
    if (input) {
      input.value = "";
      input.click();
    }
  };

  const handleAllowanceDeductionFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validExtension = /\.(xlsx|xls|csv)$/i.test(file.name);
    if (!validExtension) {
      showAlert(t("error"), t("allowanceDeductionImportError"), "error");
      return;
    }

    try {
      const data = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(new Uint8Array(e.target.result));
        reader.onerror = reject;
        reader.readAsArrayBuffer(file);
      });

      const workbook = XLSX.read(data, { type: "array" });
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(firstSheet, {
        raw: false,
        defval: "",
      });

      if (!Array.isArray(rows) || rows.length === 0) {
        throw new Error("Empty file");
      }

      // ─────────────────────────────────────────────────────────────────────────
      // Reserved header aliases — these map to the two required scalar fields.
      // Everything else is treated as an allowance or deduction column.
      // ─────────────────────────────────────────────────────────────────────────
      const INFINITY_CODE_ALIASES = new Set([
        "infcode",
        "infinity code",
        "infinitycode",
        "employee code",
        "code",
      ]);
      const DATE_ALIASES = new Set(["date", "payroll date", "period date"]);

      const records = rows
        .map((row) => {
          const record = {};

          for (const [key, rawValue] of Object.entries(row)) {
            const value =
              rawValue === null || rawValue === undefined ? "" : rawValue;
            const normalizedHeader = String(key)
              .trim()
              .toLowerCase()
              .replace(/\s+/g, " ");

            // ── reserved scalars ──────────────────────────────────────────────
            if (INFINITY_CODE_ALIASES.has(normalizedHeader)) {
              record.infinityCode = String(value).trim();
              continue;
            }
            if (DATE_ALIASES.has(normalizedHeader)) {
              record.date = String(value).trim();
              continue;
            }

            // ── skip empty values ─────────────────────────────────────────────
            const strValue = String(value).trim();
            if (strValue === "" || strValue === "0") continue;

            // ── pass every other column through as-is ─────────────────────────
            // The backend's extractFlatImportLines + new _days logic handles
            // classifying columns as allowances, deductions-by-amount, or
            // deductions-by-days.
            record[String(key).trim()] = strValue;
          }

          return record;
        })
        .filter((row) => row.infinityCode && row.date);

      if (records.length === 0) {
        throw new Error(
          "No valid records found — every row must have an infinity code and a date",
        );
      }

      const response = await apiFetch(
        `${API_BASE_URL}/payrollX/bulk/import-allowances-deductions`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ records }),
          timeout: 300000,
        },
      );

      const result = await response.json();

      if (!response.ok || !result?.success) {
        throw new Error(result?.error || "Import failed");
      }

      const processedCount = result?.data?.processed || 0;
      const totalCount = result?.data?.total || records.length;
      const failedCount = result?.data?.failed || 0;

      if (processedCount === 0) {
        const firstError = result?.data?.errors?.[0]?.error;
        throw new Error(firstError || "No records were imported");
      }

      if (failedCount > 0) {
        const firstError = result?.data?.errors?.[0]?.error || "";
        showAlert(
          t("important"),
          `${t("allowanceDeductionImportSuccess")}: ${processedCount}/${totalCount}. Failed: ${failedCount}${firstError ? `. First error: ${firstError}` : ""}`,
          "info",
        );
      } else {
        showAlert(
          t("success"),
          `${t("allowanceDeductionImportSuccess")}: ${processedCount}/${totalCount}`,
          "success",
        );
      }

      refreshData();
    } catch (error) {
      showAlert(
        t("error"),
        `${t("allowanceDeductionImportError")}: ${error.message}`,
        "error",
      );
    }
  };

  return {
    handleOpenAllowanceDeductionImport,
    handleAllowanceDeductionFileSelect,
  };
};

export default useAllowanceDeductionImport;